"use client";

import React, { useEffect } from "react";
import { usePathname } from "next/navigation";
import Link from "@/components/Link";
import styles from "./styles.module.scss";

const AccessibilityMenu: React.FC = () => {
  const pathname = usePathname();

  // Move focus to the top of the page on route changes
  useEffect(() => {
    const topId = document.querySelector<HTMLElement>("#top");
    if (topId) {
      topId.focus();
    }
  }, [pathname]);

  const pathIsHome = pathname === "/";

  const skipLinks = [
    // On the homepage this points at the #top focus target rendered below.
    // It used to point at "#home", which is not an id anywhere in the app, so
    // the first skip link resolved to nothing and failed the skip-link audit.
    { href: pathIsHome ? "#top" : "/", label: "Home", useNextLink: !pathIsHome },
    { href: "#content", label: "Content" },
    { href: "#footer", label: "Footer navigation" },
  ];

  return (
    <>
      {/*
        Invisible focus target at the very top of the page, and the destination
        of the "Home" skip link. tabIndex is set declaratively so the target is
        focusable from first paint rather than only after the effect above runs.
      */}
      <a id="top" tabIndex={-1} aria-live="polite" />

      {/*
        The nav is the wrapper, so "Skip to:" is inside a landmark, and it is
        named, so it can be told from the header's main navigation.
      */}
      <nav className={styles.accessibilityMenu} aria-label="Skip links">
        <span className={styles.label}>Skip to:</span>
        <ul>
          {skipLinks.map((link) => {
            const LinkElement = link.useNextLink ? Link : "a";
            return (
              <li key={link.href}>
                <LinkElement
                  href={link.href}
                  className={styles.link}
                  title={`Skip to ${link.label}`}
                >
                  {link.label}
                </LinkElement>
              </li>
            );
          })}
        </ul>
      </nav>
    </>
  );
};

export default AccessibilityMenu;
